/**
 * Utility functions for analyzing and processing audit decisions
 */

import type { GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';

import type { DecisionStatistics, DecisionStatus, AgreementStatus } from '../types';

/**
 * Calculate decision statistics for summary display
 * Now includes comprehensive comparison of language, transcription, labels, and corrected text
 */
export function calculateDecisionStatistics(decisions: GraphQLAuditDecision[]): DecisionStatistics {
  const totalDecisions = decisions.length;

  if (totalDecisions === 0) {
    return {
      totalDecisions: 0,
      agreementLevel: 0,
      conflictCount: 0,
      submittedCount: 0,
      draftCount: 0,
      hasConflicts: false,
    };
  }

  // Count submitted vs draft decisions
  const submittedCount = decisions.filter(decision => decision.isSelected).length;
  const draftCount = totalDecisions - submittedCount;

  // Calculate agreement level based on decision consistency
  const agreementLevel = calculateAgreementLevel(decisions);

  // Count conflicts (decisions that disagree with each other)
  const conflictCount = calculateConflictCount(decisions);

  return {
    totalDecisions,
    agreementLevel,
    conflictCount,
    submittedCount,
    draftCount,
    hasConflicts: conflictCount > 0,
  };
}

/**
 * Calculate agreement level between decisions (0-100%)
 */
function calculateAgreementLevel(decisions: GraphQLAuditDecision[]): number {
  if (decisions.length <= 1) return 100;

  // Group decisions by their core assessment
  const decisionGroups = groupDecisionsByAssessment(decisions);
  const largestGroup = Math.max(...Object.values(decisionGroups));

  return Math.round((largestGroup / decisions.length) * 100);
}

/**
 * Group decisions by their core assessment (language + transcription correctness + labels + corrected transcription)
 */
function groupDecisionsByAssessment(decisions: GraphQLAuditDecision[]): Record<string, number> {
  const groups: Record<string, number> = {};

  decisions.forEach(decision => {
    // Create a comprehensive key that includes language, transcription, labels, and corrected text
    const languageKey = decision.languageCorrect;
    const transcriptionKey = decision.transcriptionCorrect;

    // Sort labels to ensure consistent comparison regardless of order
    const originalLabels = [...(decision.originalLabels || [])].sort((a, b) => a.name.localeCompare(b.name));
    const correctedLabels = [...(decision.correctedLabels || [])].sort((a, b) => a.name.localeCompare(b.name));

    // Create label signatures for comparison
    const originalLabelsKey = originalLabels.map(l => `${l.name}:${l.severity}`).join(',');
    const correctedLabelsKey = correctedLabels.map(l => `${l.name}:${l.severity}`).join(',');

    // Include corrected transcription in the comparison (normalize empty/null values)
    const correctedTranscriptionKey = decision.correctedTranscription?.trim() || '';

    const key = `${languageKey}-${transcriptionKey}-${originalLabelsKey}-${correctedLabelsKey}-${correctedTranscriptionKey}`;

    groups[key] = (groups[key] || 0) + 1;
  });

  return groups;
}

/**
 * Calculate number of conflicting decisions
 */
function calculateConflictCount(decisions: GraphQLAuditDecision[]): number {
  if (decisions.length <= 1) return 0;

  const groups = groupDecisionsByAssessment(decisions);
  const groupCounts = Object.values(groups);

  // If all decisions agree, no conflicts
  if (groupCounts.length === 1) return 0;

  // Return number of decisions that don't match the majority
  const majorityCount = Math.max(...groupCounts);

  return decisions.length - majorityCount;
}

/**
 * Get decision status for display
 */
export function getDecisionStatus(decision: GraphQLAuditDecision): DecisionStatus {
  if (decision.isSelected) return 'submitted';

  // Check if this decision conflicts with others (would need decisions array context)
  return 'draft';
}

/**
 * Get decision status with conflict detection
 */
export function getDecisionStatusWithConflicts(decision: GraphQLAuditDecision, allDecisions: GraphQLAuditDecision[]): DecisionStatus {
  if (decision.isSelected) return 'submitted';

  // Check if this decision conflicts with the majority
  const hasConflict = isDecisionInConflict(decision, allDecisions);

  return hasConflict ? 'conflict' : 'draft';
}

/**
 * Check if a decision conflicts with the majority
 */
function isDecisionInConflict(decision: GraphQLAuditDecision, allDecisions: GraphQLAuditDecision[]): boolean {
  if (allDecisions.length <= 1) return false;

  const groups = groupDecisionsByAssessment(allDecisions);
  const decisionKey = `${decision.languageCorrect}-${decision.transcriptionCorrect}`;
  const decisionGroupSize = groups[decisionKey] || 0;
  const majoritySize = Math.max(...Object.values(groups));

  return decisionGroupSize < majoritySize;
}

/**
 * Get agreement status between decisions
 */
export function getAgreementStatus(decisions: GraphQLAuditDecision[]): AgreementStatus {
  if (decisions.length === 0) return 'unknown';
  if (decisions.length === 1) return 'agreed';

  const groups = groupDecisionsByAssessment(decisions);
  const groupCount = Object.keys(groups).length;

  if (groupCount === 1) return 'agreed';
  if (groupCount === 2) return 'partial';

  return 'disagreed';
}

/**
 * Format decision timestamp for display
 */
export function formatDecisionTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 1) {
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    return `${diffMinutes}m ago`;
  }

  if (diffHours < 24) {
    return `${Math.floor(diffHours)}h ago`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get decision type display text
 */
export function getDecisionTypeText(decision: GraphQLAuditDecision): string {
  if (decision.languageCorrect && decision.transcriptionCorrect) {
    return 'CORRECT';
  }

  if (!decision.languageCorrect) {
    return 'WRONG LANGUAGE';
  }

  if (!decision.transcriptionCorrect) {
    return decision.languageCorrect ? 'INCORRECT TRANSCRIPTION' : 'INCORRECT';
  }

  return 'PARTIALLY CORRECT';
}

/**
 * Get status icon for decision
 */
export function getDecisionStatusIcon(status: DecisionStatus): string {
  switch (status) {
    case 'submitted':
      return '✅';
    case 'draft':
      return '🟡';
    case 'conflict':
      return '🔴';
    default:
      return '📊';
  }
}
